/**
 * Cart Drawer Web Component
 * Handles cart drawer open/close and integration with product forms.
 *
 * All cart operations (add, quantity change, remove) use Section Rendering API.
 * Server returns pre-rendered HTML → renderContents() replaces drawer DOM.
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;
    this.boundClose = this.close.bind(this);

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    this.bindOverlay();

    // Delegated click handlers (survive innerHTML replacement in renderContents)
    this.addEventListener('click', (evt) => {
      // Close button
      if (evt.target.closest('.cart-drawer__close')) {
        evt.preventDefault();
        this.close();
        return;
      }

      const bundleRemoveBtn = evt.target.closest('.cart-drawer__bundle-remove');
      if (bundleRemoveBtn) {
        evt.preventDefault();
        const bundleName = bundleRemoveBtn.dataset.bundleName;
        if (bundleName && typeof window.removeBundleFromCart === 'function') {
          bundleRemoveBtn.disabled = true;
          const bundleGroup = bundleRemoveBtn.closest('.cart-drawer__bundle-group');
          if (bundleGroup) bundleGroup.classList.add('cart-item--removing');
          window.removeBundleFromCart(bundleName).catch((err) => {
            console.error('Error removing bundle:', err);
            bundleRemoveBtn.disabled = false;
            if (bundleGroup) bundleGroup.classList.remove('cart-item--removing');
          });
        }
      }
    });

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#head-cart-count');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');

    if (window.location.pathname !== routes.cart_url) {
      cartLink.setAttribute('aria-haspopup', 'dialog');

      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });

      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const wasAlreadyOpen = this.classList.contains('cart-drawer--open');
    this.classList.add('cart-drawer--open');
    document.body.classList.add('overflow-hidden');

    if (!wasAlreadyOpen) {
      this.addEventListener('transitionend', () => this.trapFocusInDrawer(), { once: true });
    } else {
      this.trapFocusInDrawer();
    }
  }

  trapFocusInDrawer() {
    const container = this.classList.contains('cart-drawer--empty')
      ? this.querySelector('.cart-drawer__empty')
      : this.querySelector('.cart-drawer__panel');

    const focusEl = this.querySelector('.cart-drawer__close');
    if (container && focusEl) {
      trapFocus(container, focusEl);
    }
  }

  close() {
    this.classList.remove('cart-drawer--open');
    document.body.classList.remove('overflow-hidden');
    if (this.activeElement) {
      removeTrapFocus(this.activeElement);
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  bindOverlay() {
    const overlay = this.querySelector('.cart-drawer__overlay');
    if (overlay) {
      overlay.removeEventListener('click', this.boundClose);
      overlay.addEventListener('click', this.boundClose);
    }
  }

  /**
   * Render cart contents from Section Rendering API response.
   * Called after add-to-cart, quantity change, and item removal.
   * Replaces entire drawer panel HTML with server-rendered Liquid.
   */
  renderContents(parsedState) {
    const sectionHtml = parsedState.sections?.['cart-drawer'];
    if (!sectionHtml) return;

    // Extract cart data from new section HTML
    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newDrawerData = parsed.querySelector('cart-drawer');
    const itemCount = parseInt(newDrawerData?.dataset.cartCount || '0', 10);

    // Update cart count badge
    updateCartCount(itemCount);

    // Update data attributes
    this.dataset.cartCount = itemCount;
    this.dataset.cartTotal = newDrawerData?.dataset.cartTotal || '0';

    // Toggle empty state
    const isEmpty = itemCount === 0;
    this.classList.toggle('cart-drawer--empty', isEmpty);

    const itemsWrapper = this.querySelector('cart-drawer-items');
    if (itemsWrapper) {
      itemsWrapper.classList.toggle('cart-drawer__items-wrapper--hidden', isEmpty);
    }

    // Replace entire drawer panel innerHTML
    const panel = this.querySelector('#CartDrawer');
    if (panel) {
      const newPanel = parsed.querySelector('#CartDrawer');
      if (newPanel) {
        panel.innerHTML = newPanel.innerHTML;
      }
    }

    // Re-bind overlay after DOM replacement, then open
    setTimeout(() => {
      this.bindOverlay();
      this.open();
    });
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

/**
 * Cart Drawer Items — <cart-drawer-items>
 * Extends CartItems with Section Rendering via parent cart-drawer.
 * Quantity changes and removals get fresh server HTML — no manual DOM updates.
 */
class CartDrawerItems extends CartItems {
  getSectionId() {
    return 'cart-drawer';
  }

  renderSection(cart) {
    const cartDrawer = this.closest('cart-drawer');
    if (cartDrawer) cartDrawer.renderContents(cart);
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
